<i18n>
{
  "de": {
    "topics": "Themen",
    "advancedFilters": "Erweiterte Filter",
    "visibility": "Sichtbarkeit",
    "showOnlyPublicPosts": "Nur öffentliche Beiträge anzeigen",
    "showOnlyFollowerPosts": "Nur Beiträge von Follower anzeigen",
    "noTopics": "Keine Themen verfügbar"
  },
  "en": {
    "topics": "Topics",
    "advancedFilters": "Advanced Filters",
    "visibility": "Visibility",
    "showOnlyPublicPosts": "Show only public posts",
    "showOnlyFollowerPosts": "Show only posts for followers",
    "noTopics": "No topics available"
  }
}
</i18n>

<template>
  <div class="filter-content">
    <!-- Topics Section -->
    <div
      v-if="postTags && postTags.length"
      class="filter-section"
    >
      <h4 class="section-title">
        <i class="icon-tags" />
        {{ $t('topics') }}
      </h4>
      <div class="topics-grid">
        <nuxt-link
          v-for="tag of postTags"
          :key="tag._id"
          :to="localePath({
            name: tagIds.includes(tag.id) ? 'id-posts' : 'id-posts-tag',
            params: tagIds.includes(tag.id) ? { id: artist.urlAlias } : { id: artist.urlAlias, tag: tag.slug },
          })"
          class="topic-chip"
          :class="{ 'active': tagIds.includes(tag.id) }"
          @click.native="onTopicClick"
        >
          {{ tag.name }}
        </nuxt-link>
      </div>
    </div>

    <!-- Advanced Filters Section -->
    <div class="filter-section">
      <h4 class="section-title">
        <i class="icon-sliders" />
        {{ $t('advancedFilters') }}
      </h4>
      <div class="advanced-filters">
        <div class="filter-item">
          <filterPostsByReward
            :layout="layout"
            :clear="clearFilterByReward"
            :bottom-sheet-mode="inBottomSheet"
            @filter="handleFilter"
          />
        </div>
        <div class="filter-item">
          <filterPostsByMonth
            :layout="layout"
            :bottom-sheet-mode="inBottomSheet"
            @filter="handleFilter"
          />
        </div>
        <div class="filter-item">
          <filterPostsByContent
            :layout="layout"
            :bottom-sheet-mode="inBottomSheet"
            @filter="handleFilter"
          />
        </div>
      </div>
    </div>

    <!-- Visibility Section -->
    <div class="filter-section">
      <h4 class="section-title">
        <i class="icon-eye" />
        {{ $t('visibility') }}
      </h4>
      <div class="visibility-options">
        <label class="visibility-option">
          <input
            id="show-public-posts"
            type="checkbox"
            class="option-checkbox"
            name="show-public-posts"
            :checked="filter.public"
            @change="showOnlyPublicPosts"
          >
          <span class="checkmark" />
          <span class="option-text">{{ $t('showOnlyPublicPosts') }}</span>
        </label>

        <label class="visibility-option">
          <input
            id="show-follower-posts"
            type="checkbox"
            class="option-checkbox"
            name="show-follower-posts"
            :checked="filter.limitToFollowers"
            @change="showOnlyFollowerPosts"
          >
          <span class="checkmark" />
          <span class="option-text">{{ $t('showOnlyFollowerPosts') }}</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import filterPostsByReward from '~/components/filter-posts-by-rewards'
import filterPostsByMonth from '~/components/filter-posts-by-month'
import filterPostsByContent from '~/components/filter-posts-by-content'

export default {
  name: 'FilterPostsContent',
  components: {
    filterPostsByReward,
    filterPostsByMonth,
    filterPostsByContent
  },
  props: {
    layout: {
      type: String,
      default: 'mobileBottomSheet'
    },
    inBottomSheet: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    filter: {
      tags: [],
      product: [],
      reward: [],
      months: [],
      contents: [],
      public: false,
      limitToFollowers: false
    }
  }),
  computed: {
    clearFilterByReward() {
      return !this.filter.public && !this.filter.limitToFollowers
    },
    tagIds() {
      if (!this.postFilter) return []
      return this.postFilter.tags.map((t) => t.id)
    },
    ...mapState(['artist', 'profilePostsLoaded', 'profilePostsLoading', 'postFilter', 'postTags'])
  },
  methods: {
    resetAllFilters() {
      this.filter = {
        tags: [],
        product: [],
        reward: [],
        months: [],
        contents: [],
        public: false,
        limitToFollowers: false
      }
      this.resetPostFilter()
      this.executeFilter()

      // Navigate to base posts URL to clear any tag filters
      this.$router
        .push(this.localePath({ name: 'id-posts', params: { id: this.artist.urlAlias } }))
        .catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
    },
    onTopicClick() {
      this.$emit('filtersChanged')
      // Close bottom sheet after topic selection
      if (this.inBottomSheet) {
        setTimeout(() => {
          this.$parent.closeBottomSheet?.()
        }, 100)
      }
    },
    showOnlyPublicPosts(event) {
      this.filter.public = event.target.checked
      this.filter.limitToFollowers = false
      this.filter.product = []
      this.filter.reward = []
      this.executeFilter()
      this.$emit('filtersChanged')
    },
    showOnlyFollowerPosts(event) {
      this.filter.limitToFollowers = event.target.checked
      this.filter.public = false
      this.filter.product = []
      this.filter.reward = []
      this.executeFilter()
      this.$emit('filtersChanged')
    },
    handleFilter(filter) {
      this.filter = { ...this.postFilter }
      if (
        (filter.product || []).length ||
        (filter.reward || []).length
      ) {
        this.filter.public = false
        this.filter.limitToFollowers = false
      }

      Object.keys(filter)
        .filter((key) => !['public', 'limitToFollowers'].includes(filter[key]))
        .forEach((key) => {
          this.filter[key] = filter[key]
        })

      this.executeFilter()
      this.$emit('filtersChanged')
    },
    async executeFilter() {
      if (!this.filter) return
      this.setPostFilter({ filter: this.filter })
      if (!this.profilePostsLoading && !this.profilePostsLoaded) {
        await this.loadAllPosts({
          urlAlias: this.artist.urlAlias
        })
      }
    },
    ...mapActions(['loadAllPosts', 'setPostFilter', 'resetPostFilter'])
  },
  async mounted() {
    this.filter = Object.assign({}, this.postFilter)
    this.executeFilter()

    // Listen for reset events
    this.$root.$on('resetAllFilters', () => {
      this.resetAllFilters()
    })
  }
}
</script>

<style lang="scss" scoped>
@import 'assets/sass/_variables';

.filter-content {
  padding: 10px 0;
}

.filter-section {
  margin-bottom: 30px;

  &:last-child {
    margin-bottom: 10px;
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 15px 0;

  i {
    color: var(--primary-color, #fb3e52);
    font-size: 14px;
  }
}

.topics-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.topic-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  border-radius: 5px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  text-decoration: none;

  // Default state - semi-transparent with text color
  background: color-mix(
    in hsl,
    var(--tiles-background-color, #fff),
    var(--tiles-background-text-color, #333) 8%
  );
  color: var(--tiles-background-text-color, #333);

  // Subtle inner glow effect
  box-shadow:
    inset 0 0 0 1px color-mix(
      in hsl,
      var(--tiles-background-color, #fff),
      var(--tiles-background-text-color, #333) 15%
    );

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      inset 0 0 0 1px color-mix(
        in hsl,
        var(--tiles-background-color, #fff),
        var(--tiles-background-text-color, #333) 25%
      ),
      0 4px 12px color-mix(
        in hsl,
        var(--tiles-background-color, #fff),
        black 15%
      );
  }

  // Active state - filled with primary color
  &.active {
    background: var(--primary-color, #fb3e52);
    color: var(--primary-text-color, #fff);
    box-shadow:
      0 4px 15px color-mix(
        in hsl,
        var(--primary-color, #fb3e52),
        transparent 50%
      );

    &:hover {
      transform: translateY(-2px);
      box-shadow:
        0 6px 20px color-mix(
          in hsl,
          var(--primary-color, #fb3e52),
          transparent 40%
        );
    }
  }
}

.advanced-filters {
  display: flex;
  flex-direction: column;
  gap: 10px;

  @media screen and (min-width: 400px) {
    flex-direction: row;
    gap: 8px;
  }

  .filter-item {
    flex: 1;
    min-width: 0; // Allows items to shrink below content width

    @media screen and (min-width: 400px) {
      margin-bottom: 0;
    }

    &:not(:last-child) {
      margin-bottom: 10px;

      @media screen and (min-width: 400px) {
        margin-bottom: 0;
        margin-right: 0;
      }
    }

    // Dropdown positioning for mobile row layout (only when in row mode)
    @media screen and (min-width: 400px) {
      // First filter (Offers) - dropdown aligns to left edge of button
      &:nth-child(1) {
        :deep(.checkboxLayer) {
          left: 0 !important;
          right: auto !important;
        }
      }

      // Second filter (Months) - dropdown centered below button
      &:nth-child(2) {
        :deep(.checkboxLayer) {
          left: 50% !important;
          right: auto !important;
          transform: translateX(-50%);
        }
      }

      // Third filter (Content) - dropdown aligns to right edge of button
      &:nth-child(3) {
        :deep(.checkboxLayer) {
          right: 0 !important;
          left: auto !important;
        }
      }
    }
  }
}

.visibility-options {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.visibility-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  position: relative;
  padding-left: 35px;

  .option-checkbox {
    position: absolute;
    opacity: 0;
    cursor: pointer;
  }

  .checkmark {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 20px;
    width: 20px;
    background-color: #f8f9fa;
    border: 2px solid #dee2e6;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:after {
      content: "";
      position: absolute;
      display: none;
      left: 6px;
      top: 2px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }

  .option-text {
    font-size: 14px;
    color: #495057;
    line-height: 1.4;
  }

  &:hover .checkmark {
    background-color: #e9ecef;
    border-color: #adb5bd;
  }

  .option-checkbox:checked ~ .checkmark {
    background-color: var(--primary-color, #fb3e52);
    border-color: var(--primary-color, #fb3e52);

    &:after {
      display: block;
    }
  }
}

// Bottom sheet specific styles
.bottom-sheet-content {
  :deep(.multi-select) {
    margin-bottom: 0;

    .btn-select {
      border-radius: 8px;
      border: 2px solid #e9ecef;
      padding: 12px 16px;
      font-size: 14px;

      &:hover {
        border-color: var(--primary-color, #fb3e52);
      }
    }

    .checkboxLayer {
      border-radius: 8px;
      border: 2px solid #e9ecef;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
